"use client"

import {
  getDefaultConfig,
  RainbowKitProvider,
  Theme,
} from "@rainbow-me/rainbowkit"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { http, WagmiProvider } from "wagmi"
import {
  hardhat,
  mainnet,
  sepolia,
  arbitrum,
  arbitrumSepolia,
  optimism,
  base,
  baseSepolia,
  optimismSepolia,
} from "wagmi/chains"
import { blessnet, blessnetSepolia } from "./blessnet-chain-configs"
import { VaultProvider } from "./components/useVault"
import { IS_PROD } from "../IS_PROD"
import ErrorBoundary from "../utils/client/ErrorBoundary"

// @ts-ignore that we add a func to this prototype
// to avoid an error in saving bigints to localStorage
// eslint-disable-next-line no-extend-native
BigInt.prototype.toJSON = function toJSON() {
  return this.toString()
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
    },
  },
})
const chains = (() => {
  if (IS_PROD) {
    return [mainnet, blessnet, optimism, base, arbitrum]
  }
  if (process.env.NODE_ENV === "development") {
    return [
      // blessnetSepolia,
      hardhat,
      sepolia,
      arbitrumSepolia,
      arbitrumSepolia,
      baseSepolia,
      optimismSepolia,
    ]
  }
  return [
    sepolia,
    // blessnetSepolia,
    arbitrumSepolia,
    arbitrum,
    baseSepolia,
    optimismSepolia,
  ]
  // we want hardhat as last so that it defaults to blessnet
})()
  .filter(Boolean)
  .flat() as any

const config = getDefaultConfig({
  projectId: process.env.NEXT_PUBLIC_RAINBOW_PROJECT_ID as string,
  ssr: false,

  appName: "loomlock",
  chains: chains as any,
  transports: {
    [mainnet.id]: http(
      `https://eth-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [sepolia.id]: http(
      `https://eth-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [blessnet.id]: http(blessnet.rpcUrls.default.http[0]),
    [blessnetSepolia.id]: http(blessnetSepolia.rpcUrls.default.http[0]),
    [arbitrum.id]: http(
      `https://arb-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [arbitrumSepolia.id]: http(
      `https://arb-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [optimism.id]: http(
      `https://opt-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [base.id]: http(
      `https://base-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [baseSepolia.id]: http(
      `https://base-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [optimismSepolia.id]: http(
      `https://opt-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
    ),
    [hardhat.id]: http("http://localhost:8545"),
  },
})
/**
 * Provides a context for interacting with various crypto-related services and libraries.
 *
 * The `Web3Provider` component encapsulates multiple providers required for managing crypto-related
 * interactions. It includes:
 *
 * - `WagmiProvider`: For managing interactions with Ethereum wallets and contracts.
 * - `QueryClientProvider`: For managing data fetching and caching with React Query.
 * - `RainbowKitProvider`: For managing wallet connections and UI with RainbowKit.
 *
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 * @returns {React.ReactElement} The rendered `CryptoProvider` component with the specified context providers.
 */
export const Web3Provider = ({
  children,
  theme,
}: {
  children: React.ReactNode
  theme?: Theme
}): React.ReactElement => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          coolMode={false}
          theme={theme}
          modalSize="compact"
          showRecentTransactions
        >
          <ErrorBoundary>
            <VaultProvider>{children}</VaultProvider>
          </ErrorBoundary>
        </RainbowKitProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
      </QueryClientProvider>
    </WagmiProvider>
  )
}

Web3Provider.displayName = "Web3Provider"
